/*
 * Назначение: левая навигация админки.
 * Функционал: переключатель проекта (реальные данные), меню разделов на
 * NavLink из react-router (подсветка активного, нормальные ссылки),
 * системные ссылки, кнопка сворачивания.
 */

import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Database,
  LayoutDashboard,
  MessageSquare,
  Plug,
  Plus,
  Settings,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Link, NavLink } from "react-router";

import { cn } from "@/lib/utils";
import { useProject } from "../project/project-provider";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const menuItems = [
  { icon: LayoutDashboard, label: "Дашборд", path: "/dashboard" },
  { icon: Database, label: "База знаний", path: "/knowledge-base" },
  { icon: MessageSquare, label: "Диалоги", path: "/dialogs" },
  { icon: ClipboardList, label: "Заявки", path: "/leads" },
  { icon: BarChart3, label: "Аналитика", path: "/analytics" },
  { icon: Settings, label: "Настройки", path: "/settings" },
];

const systemMenuItems = [
  { icon: Plug, label: "Интеграции", path: "/integrations" },
  { icon: ShieldCheck, label: "Безопасность", path: "/security" },
  { icon: Users, label: "Пользователи", path: "/users" },
];

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

function navItemClass(isActive: boolean): string {
  return cn(
    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
    isActive
      ? "bg-sidebar-primary text-sidebar-primary-foreground"
      : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
  );
}

export function Sidebar({ collapsed = false, onToggleCollapse }: SidebarProps) {
  const { projects, currentSlug, setCurrent, loading } = useProject();

  return (
    <aside
      className={cn(
        "h-screen bg-sidebar border-r border-sidebar-border flex flex-col transition-all duration-300",
        collapsed ? "w-16" : "w-60",
      )}
    >
      <div className="p-4 border-b border-sidebar-border">
        {!collapsed ? (
          <div className="space-y-2">
            <Select
              value={currentSlug ?? undefined}
              onValueChange={(slug) => slug && setCurrent(slug)}
              disabled={loading || projects.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={loading ? "Загрузка…" : "Нет проектов"} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.slug}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="w-full"
            >
              <Link to="/projects/new">
                <Plus className="h-4 w-4 mr-2" />
                Создать проект
              </Link>
            </Button>
          </div>
        ) : (
          <Button asChild variant="ghost" size="icon" className="w-full">
            <Link to="/projects/new" title="Создать проект">
              <Plus className="h-4 w-4" />
            </Link>
          </Button>
        )}
      </div>

      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/dashboard"}
              className={({ isActive }) => navItemClass(isActive)}
              title={item.label}
            >
              <Icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          );
        })}

        <div className="pt-4 mt-4 border-t border-sidebar-border">
          <div className="px-3 py-2 text-xs font-medium text-muted-foreground">
            {!collapsed && "Системные"}
          </div>
          {systemMenuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => navItemClass(isActive)}
                title={item.label}
              >
                <Icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {onToggleCollapse && (
        <div className="p-2 border-t border-sidebar-border">
          <Button
            variant="ghost"
            size="icon"
            className="w-full"
            onClick={onToggleCollapse}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}
    </aside>
  );
}
